import React, { useMemo } from "react";
import RuleIcon from "@mui/icons-material/Rule";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonRemoveAlt1OutlinedIcon from "@mui/icons-material/PersonRemoveAlt1Outlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Box, Paper, Stack, Typography } from "@mui/material";
import DataTable, {
  ColumnDef,
} from "../../../../../components/DataTable/DataTable";
import { brand, gray } from "../../../../../theme/themePrimitives";
import { filterImpactEnum } from "../../../../../services/api/types/rules/rules";

export type PreviewUser = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  impact: filterImpactEnum;
};

const StepCircle = ({ number }: { number: number }) => (
  <Box
    sx={{
      width: 28,
      height: 28,
      borderRadius: "50%",
      bgcolor: brand[600],
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Typography sx={{ color: "white", fontWeight: 600, fontSize: 14 }}>
      {number}
    </Typography>
  </Box>
);

type LivePreviewProps = {
  hasFilters: boolean;
  data: PreviewUser[];
  totalCount: number;
  addedCount: number;
  removedCount: number;
  page: number;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (rows: number) => void;
  search: string;
  onSearchChange: (v: string) => void;
  isLoading?: boolean;
};

export const LivePreview: React.FC<LivePreviewProps> = ({
  hasFilters,
  data,
  totalCount,
  addedCount,
  removedCount,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  search,
  onSearchChange,
  isLoading,
}) => {
  const columns = useMemo<ColumnDef<PreviewUser>[]>(
    () => [
      {
        id: "firstName",
        label: "First name",
        getValue: (row) => row.firstName,
      },
      { id: "lastName", label: "Last name", getValue: (row) => row.lastName },
      { id: "email", label: "Email", getValue: (row) => row.email },
      {
        id: "impact",
        label: "Filter Impact",
        render: (row) => {
          if (row.impact === "Added")
            return (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.25,
                  border: "1px solid #ABEFC6",
                  borderRadius: "20px",
                  bgcolor: "#ECFDF3",
                  color: "#067647",
                  fontSize: 13,
                }}
              >
                <PersonAddAlt1OutlinedIcon sx={{ fontSize: 14 }} />
                Added
              </Box>
            );
          if (row.impact === "Removed")
            return (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.25,
                  border: "1px solid #FECDCA",
                  borderRadius: "20px",
                  bgcolor: "#FEF3F2",
                  color: "#B42318",
                  fontSize: 13,
                }}
              >
                <PersonRemoveAlt1OutlinedIcon sx={{ fontSize: 14 }} />
                Removed
              </Box>
            );
          if (row.impact === filterImpactEnum.ASSIGNED_BY_OTHER_RULE)
            return (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.25,
                  border: "1px solid #B2DDFF",
                  borderRadius: "20px",
                  bgcolor: "#EFF8FF",
                  color: "#175CD3",
                  fontSize: 13,
                }}
              >
                <SwapHorizIcon sx={{ fontSize: 14 }} />
                Added by other rule
              </Box>
            );
          return (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                py: 0.25,
                border: `1px solid ${gray[300]}`,
                borderRadius: "20px",
                bgcolor: gray[50],
                color: gray[600],
                fontSize: 13,
              }}
            >
              Unchanged
            </Box>
          );
        },
      },
    ],
    [],
  );

  return (
    <Paper sx={{ p: 3, borderRadius: "12px" }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        mb={3}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <StepCircle number={3} />
          <Typography variant="h6" fontWeight={600}>
            Live preview
          </Typography>
        </Stack>
        {hasFilters && (
          <Stack direction="row" gap={1.5}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                border: "1px solid #ABEFC6",
                borderRadius: "9999px",
                bgcolor: "#ECFDF3",
                color: "#067647",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              + {addedCount} added
            </Box>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                border: "1px solid #FECDCA",
                borderRadius: "9999px",
                bgcolor: "#FEF3F2",
                color: "#F04438",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              - {removedCount} removed
            </Box>
          </Stack>
        )}
      </Box>

      <DataTable<PreviewUser>
        data={hasFilters ? data : []}
        columns={columns}
        totalCount={hasFilters ? totalCount : 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        {...(hasFilters && {
          search,
          onSearchChange,
          searchPlaceholder: "Search by name",
        })}
        getRowId={(r) => r.id}
        hideHeader={!hasFilters}
        isLoading={isLoading}
        renderEmptyState={
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1}
            py={4}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "#F9FAFB",
                borderRadius: "50%",
                border: "1px solid #F3F4F6",
                width: 40,
                height: 40,
                boxShadow: "0px 1px 2px 0px #1018280D",
              }}
            >
              <RuleIcon sx={{ fontSize: 20, color: "#98A2B3" }} />
            </Box>
            <Typography fontSize={16} fontWeight={600} color="#374151">
              No rules configured yet
            </Typography>
            <Typography fontSize={14} color="#475467" textAlign="center">
              Add your first rule to automatically assign this course to users
              based on their attributes.
            </Typography>
          </Box>
        }
      />
    </Paper>
  );
};
